// Put a player's look on a character sprite (character look, 2026-10-29). A default look, the Canvas renderer, or a
// texture with no mask (yet) draws through the default pipeline — exactly the art, as before this feature.
import { lookUniforms } from './lookPalette.mjs';
import { resolveLook } from './lookCatalog.mjs';
import { MASKED } from './lookMasks.mjs';
import { assetUrl } from '../assetVersion.js';

const WEBGL = 2;                // Phaser.WEBGL (this module stays Phaser-free for the node tests)
const NEAREST = 1;              // Phaser.Textures.FilterMode.NEAREST

/** `<key>_mask` when the texture has a mask atlas, else null. */
export const maskKeyFor = (textureKey) => (MASKED.has(textureKey) ? `${textureKey}_mask` : null);

/** Where `<key>_mask.webp` lives: beside its atlas (scripts/anim/look_masks.py). Phaser's textures come from blob:
 *  URLs, so the folder follows from the key the way the game loads each family. */
export function maskUrl(textureKey) {
  if (textureKey.startsWith('portrait/')) return assetUrl(`/assets/anim/portrait/${textureKey.slice(9)}_mask.webp`);
  const folder = textureKey.startsWith('bike_') ? 'bike'
    : textureKey.startsWith('driver_') ? 'characters' : 'anim/packed';
  return assetUrl(`/assets/${folder}/${textureKey}_mask.webp`);
}

// One uniforms object per look, so every sprite in that look shares it and LookPipeline keeps them in one batch.
const memo = new Map();
function uniformsFor(character, look) {
  const key = `${character}:${JSON.stringify(resolveLook(character, look))}`;
  if (!memo.has(key)) memo.set(key, lookUniforms(character, look));
  return memo.get(key);
}

function reset(go) {
  if (go.pipeline?.name === 'Look') go.resetPipeline(true);
  return false;
}

/** Recolour `go` for `look`; true when the Look pipeline is on, false when it draws as the plain art. */
export function applyLook(go, character, look) {
  const uniforms = uniformsFor(character, look);
  const maskKey = maskKeyFor(go.texture?.key);
  if (!uniforms || go.scene?.sys?.renderer?.type !== WEBGL || !maskKey || !go.scene.textures.exists(maskKey)) {
    return reset(go);
  }
  go.setPipeline('Look', { uniforms, maskKey });
  return true;
}

/** Load the masks of `textureKeys` that are not loaded yet, NEAREST; then `cb()`. */
export function ensureMasks(scene, textureKeys, cb) {
  const missing = [];
  for (const key of textureKeys) {
    const mk = maskKeyFor(key);
    if (!mk || scene.textures.exists(mk)) continue;
    missing.push(mk);
    scene.load.image(mk, maskUrl(key));
  }
  if (!missing.length) { cb(); return; }
  scene.load.once('complete', () => {
    for (const mk of missing) if (scene.textures.exists(mk)) scene.textures.get(mk).setFilter(NEAREST);
    cb();
  });
  if (!scene.load.isLoading()) scene.load.start();
}
